import { MouseEvent } from "react";
import {
  Button,
  Flex,
  Modal,
  ModalProps,
  Table,
  TableProps,
  Tooltip,
  Typography,
} from "antd";
import { useJournalQuery } from "@entities/journal";
import type { components } from "@entities/journal";
import {
  CommentIcon,
  PendingSignIcon,
  RejectedSignIcon,
  SuccessfulSignIcon,
} from "@shared/assets/svg/icons";
import { UserBubble } from "@shared/ui";
import { fallbackString, formatDate, getUserFio } from "@shared/utils";
import "./record-history-modal.scss";

type SectionTwoListEntry =
  components["schemas"]["SectionTwoVersionItemForListResponse"];

interface RecordHistoryModalProps extends Omit<ModalProps, "children"> {
  section2ListId: string;
  noVersionId: string;
}

const columns: TableProps<SectionTwoListEntry>["columns"] = [
  {
    title: "Наименование специального журнала",
    render: (_, record) => (
      <Flex vertical>
        <Typography className="record-history-table-author">
          <Typography.Text className="record-history-table-author-name">
            Мокослав {/* {getUserFio(record.signer)} */}
          </Typography.Text>
          <Typography.Text className="record-history-table-author-date">
            15.11.2024 {/* {record.action.} */}
          </Typography.Text>
          <Typography.Text type="secondary">14:51 МСК</Typography.Text>
        </Typography>
        <Typography className="record-history-table-name">
          {record.specialJournalName}
        </Typography>
      </Flex>
    ),
  },
  {
    title: "Дата создания журнала",
    dataIndex: "specialJournalDate",
    render: (date: string) => (
      <Typography className="record-history-table-creation">
        {fallbackString(formatDate(date))}
      </Typography>
    ),
  },
  {
    title: "Организация, уполномоченный сотрудник, ведущий журнал",
    dataIndex: "journalKeepersList",
    render: (keepers: SectionTwoListEntry["journalKeepersList"]) => {
      if (Array.isArray(keepers)) {
        return (
          <Flex
            vertical
            gap="16px"
            className="record-history-table-organization"
          >
            {keepers.map((keeper) => (
              <Flex vertical>
                <Typography.Text>{keeper.company?.fullName}</Typography.Text>
                {keeper.authorizedRepresentativesList?.map((representative) => (
                  <>
                    <UserBubble userId={representative.userId}>
                      <Typography.Link className="journal-table-link">
                        {getUserFio(representative)}
                      </Typography.Link>
                    </UserBubble>
                    <Typography.Text>{representative.position}</Typography.Text>
                  </>
                ))}
              </Flex>
            ))}
          </Flex>
        );
      }
    },
  },
  {
    title: "Дата передачи журнала",
    dataIndex: "dateHandover",
    render: (date: SectionTwoListEntry["dateHandover"]) => (
      <Typography className="record-history-table-date">
        {fallbackString(formatDate(date))}
      </Typography>
    ),
  },
  {
    title:
      "Уполномоченный представитель застройщика или технического заказчика, лица, ответственного за эксплуатацию здания, сооружения, и (или) регионального оператора",
    dataIndex: "signer",
    render: (value: SectionTwoListEntry["signer"], record) => {
      let icon;

      switch (record.status) {
        case "AWAITING_SIGNATURE":
          icon = <PendingSignIcon />;
          break;
        case "REJECTED":
          icon = (
            <Flex align="center" gap="8px">
              <RejectedSignIcon />
              {(() => {
                 // @ts-expect-error comments отсутствуют в типе записи истории
                const comments = record?.comments || [];
                const validComments = comments.filter(
                  (comment: any) => comment?.comment && comment.comment.trim(),
                );
                return validComments.length > 0 ? (
                  <Tooltip
                    title={validComments
                      .map((comment: any) => comment.comment)
                      .join("\n")}
                  >
                    <span className="journal-table-comment-trigger">
                      <CommentIcon viewBox="0 0 20 20" width="20" height="20" />
                    </span>
                  </Tooltip>
                ) : null;
              })()}
            </Flex>
          );
          break;
        case "SIGNED":
          icon = <SuccessfulSignIcon />;
          break;
        default:
          icon = null;
      }

      return (
        <Flex className="record-history-table-representative" vertical>
          <Flex
            align="center"
            justify="flex-end"
            className="record-history-table-version"
          >
            <Typography.Text>Версия {record.versionNumber}</Typography.Text>
          </Flex>
          <Flex gap="8px" align="center">
            <UserBubble userId={value?.userId}>
              <Typography.Link className="journal-table-link">
                {getUserFio(value)}
              </Typography.Link>
            </UserBubble>
            {icon}
          </Flex>
          <Typography>{value?.position}</Typography>
        </Flex>
      );
    },
  },
];

export function RecordHistoryModal({
  onCancel,
  section2ListId,
  noVersionId,
  ...rest
}: RecordHistoryModalProps) {
  const { data: history, isLoading } = useJournalQuery(
    "get",
    "/api/v1/journals/section-two/{section2ListId}/items/{noVersionId}/versions",
    { params: { path: { section2ListId, noVersionId } } },
  );

  return (
    <Modal
      title={<Typography.Title level={4}>Версии записи</Typography.Title>}
      footer={
        <Flex>
          <Button
            type="primary"
            onClick={(e: MouseEvent<HTMLButtonElement>) =>
              onCancel && onCancel(e)
            }
          >
            Закрыть
          </Button>
        </Flex>
      }
      width="1080px"
      onCancel={onCancel}
      {...rest}
    >
      <Table
        rootClassName="record-history-table"
        columns={columns}
        dataSource={history?.section2List}
        loading={isLoading}
      />
    </Modal>
  );
}
